import { useMemo, useRef, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro"

import { cursoCorto } from "../../../../../utils/cursoCorto"
import { horarioBonito } from "../../../../../utils/horarioBonito"
import { ENROLLMENT_STATUS } from "../../../../../constants"

import "./paseDeLista.css"
import { ahoraCdmx } from "../../../../../utils/horaMexico"

/*
	Pase de lista en telefono.

	La tabla de escritorio, en un telefono, se encogia a 10px y escondia columnas.
	Aqui cada alumno es una fila alta con dos botones.

	Decisiones de Esteban (2026-09-16):
	- Sin "todos presentes" y sin estado inicial. Un interruptor que ya viene en
	  una posicion es lo mismo que marcar a todos: se guarda sin mirar. Dos botones
	  vacios obligan a decidir uno por uno.
	- La barra de abajo dice solo cuantos faltan.
	- La foto de la clase se hace visible: ya era obligatoria para registrar una
	  asistencia nueva, pero la pantalla no lo decia y Guardar se quedaba gris sin
	  explicar por que.
*/

type Props =
{
	selectedSchedule    : any
	selectedSession     : any
	selectedAttendance  : any[]
	editableAttendance  : any
	toggleStudentAttendance : (enrollmentId : number) => void
	showNoteModal       : (matricula : any) => void
	idleLocked          : boolean
	isNewAttendance     : boolean
	uploadDocuments     : File[]
	fileChange          : (archivos : File[]) => void
	onGuardar           : () => void
	puedeGuardar        : boolean
	usuario?            : string
	onEstudianteExtra?  : () => void
	descargables?       : any[]
	onDescargar?        : (documento : any) => void
}

const PaseDeLista = ({
	selectedSchedule, selectedSession, selectedAttendance, editableAttendance,
	toggleStudentAttendance, showNoteModal, idleLocked, isNewAttendance,
	uploadDocuments, fileChange, onGuardar, puedeGuardar,
	usuario, onEstudianteExtra, descargables, onDescargar
} : Props) =>
{
	/*
		Quien ya recibio un toque. En una asistencia que ya estaba registrada todos
		cuentan como decididos, porque el valor guardado ya es una decision; en una
		nueva se empieza de cero.
	*/
	const [tocados, setTocados] = useState<{[id : number] : boolean}>({})
	const archivo = useRef<HTMLInputElement>(null)

	/*
		Cada foto nueva REEMPLAZABA a la anterior, asi que solo se podia subir una:
		las maestras tenian que tomar dos, una para la app y otra para el grupo.
		Ahora se acumulan. Ademas se limpia el valor del input para que se pueda
		volver a elegir el mismo archivo. (Esteban, 2026-09-16)
	*/
	/*
		El archivo llegaba con el nombre que le pone el telefono ("IMG_20260909_11...").
		Se renombra con lo que hace falta para saber de que es sin abrirlo: curso,
		dia de la sesion, quien la subio y a que hora exacta. (Esteban, 2026-09-16)
	*/
	const nombreDeFoto = (original : File) : string =>
	{
		const ahora = ahoraCdmx()
		const reloj = String(ahora.getHours()).padStart(2, "0") + "." +
			String(ahora.getMinutes()).padStart(2, "0")
		const extension = (original.name.split(".").pop() || "jpg").toLowerCase()

		const partes = [
			cursoCorto(selectedSchedule?.course),
			selectedSession?.sessionOn,
			usuario,
			reloj
		]
			.map(parte => String(parte || "").replace(/[\/\\:*?"<>|]+/g, " ").replace(/\s+/g, " ").trim())
			.filter(Boolean)

		return partes.join(" - ") + "." + extension
	}

	const agregarFotos = (evento : React.ChangeEvent<HTMLInputElement>) =>
	{
		const nuevas = Array.from(evento.target.files || [])
			.map(archivo => new File([archivo], nombreDeFoto(archivo), {type : archivo.type}))

		if(nuevas.length > 0) fileChange([...(uploadDocuments || []), ...nuevas])

		evento.target.value = ""
	}

	const activos = useMemo
	(
		() => (selectedAttendance || []).filter
			((m : any) => m.enrollmentStatusId === ENROLLMENT_STATUS.ACTIVE),
		[selectedAttendance]
	)

	const estaDecidido = (matricula : any) : boolean =>
		!isNewAttendance || tocados[matricula.enrollmentId] === true

	const marcar = (matricula : any, presente : boolean) =>
	{
		const actual = editableAttendance?.[matricula.enrollmentId]?.isPresent === true

		if(actual !== presente) toggleStudentAttendance(matricula.enrollmentId)

		setTocados(previo => ({...previo, [matricula.enrollmentId] : true}))
	}

	const decididos = activos.filter(estaDecidido).length
	const presentes = activos.filter
		((m : any) => estaDecidido(m) && editableAttendance?.[m.enrollmentId]?.isPresent === true).length

	const faltanPersonas = activos.length - decididos
	const faltaFoto      = isNewAttendance && (uploadDocuments || []).length === 0

	const recado =
		faltanPersonas > 0
			? "Faltan " + faltanPersonas + " alumno" + (faltanPersonas === 1 ? "" : "s")
			: (faltaFoto ? "Falta la foto" : "Todo listo")

	const habilitado = !idleLocked && puedeGuardar && faltanPersonas === 0 && !faltaFoto

	return <div className="waPase">
		<div className="waPaseCabecera">
			{
				!selectedSession ? null : <span className="waPaseChip">
					{selectedSession.sessionOn}
				</span>
			}
			<h3>
				{cursoCorto(selectedSchedule?.course)}
			</h3>
			<p>
				{horarioBonito(selectedSchedule?.name)}
				{activos.length > 0 ? " · " + activos.length + " alumnos" : ""}
			</p>
		</div>

		<div className="waPaseCuenta">
			<span>
				<b>{decididos}</b> de {activos.length}
				<small>marcados</small>
			</span>
			<span className="waPasePresentes">
				<b>{presentes}</b>
				<small>presentes</small>
			</span>
		</div>

		<div className="waPaseLista">
			{
				activos.map((matricula : any) =>
				{
					const editable  = editableAttendance?.[matricula.enrollmentId]
					const presente  = editable?.isPresent === true
					const decidido  = estaDecidido(matricula)
					const debe      = matricula?.generalBalance?.expiredMonthlyBalance?.value > 0
					const resumen   = editable?.attendanceSummary

					return <div className="waPaseFila" key={"PL_" + matricula.enrollmentId}>
						<div className="waPaseAlumno">
							<span className="waPaseNombre">
								{matricula.firstName} {matricula.lastName}
								{debe ? <span className="waPaseDebe">Debe</span> : null}
							</span>
							<span className="waPaseResumen">
								{
									!resumen ? "" :
										resumen.attendancePercentage + " · " +
										resumen.attendedSessions + " de " + resumen.totalSessions + " sesiones"
								}
							</span>
						</div>

						<div className="waPaseBotones">
							<button
								type="button"
								disabled={idleLocked}
								className={decidido && presente ? "waSi activo" : "waSi"}
								aria-pressed={decidido && presente}
								onClick={() => marcar(matricula, true)}
							>
								Presente
							</button>
							<button
								type="button"
								disabled={idleLocked}
								className={decidido && !presente ? "waNo activo" : "waNo"}
								aria-pressed={decidido && !presente}
								onClick={() => marcar(matricula, false)}
							>
								Ausente
							</button>
						</div>

						<button
							type="button"
							className="waPaseNota"
							disabled={idleLocked}
							onClick={() => showNoteModal(editable)}
						>
							<FontAwesomeIcon icon={solid("note-sticky")} />
							{editable?.attendanceNotes ? " Ver nota" : " Agregar nota"}
						</button>
					</div>
				})
			}
		</div>

		{
			<div className="waPaseFoto">
				<input
					ref={archivo}
					type="file"
					accept="image/*"
					multiple
					style={{display : "none"}}
					onChange={agregarFotos}
				/>
				{
					(uploadDocuments || []).length === 0
						?
						<button type="button" className="waPaseFotoVacia" disabled={idleLocked}
							onClick={() => archivo.current?.click()}
						>
							<FontAwesomeIcon icon={solid("camera")} size="lg" />
							<span className="waPaseFotoTitulo">Foto de la clase</span>
							<span className="waPaseFotoNota">
								{isNewAttendance ? "Obligatoria. Puedes subir varias." : "Puedes subir varias."}
							</span>
						</button>
						:
						<div className="waPaseFotoLista">
							<span className="waPaseFotoMarca">
								<FontAwesomeIcon icon={solid("check")} />
							</span>
							<span className="waPaseFotoTexto">
								<b>{uploadDocuments.length === 1 ? "1 foto lista" : uploadDocuments.length + " fotos listas"}</b>
								<small>{uploadDocuments.map(f => f.name).join(", ").slice(0, 60)}</small>
							</span>
							<button type="button" disabled={idleLocked} onClick={() => archivo.current?.click()}>
								Agregar otra
							</button>
						</div>
				}
			</div>
		}

		<div className="waPaseExtras">
			{
				!onEstudianteExtra ? null :
					<button type="button" disabled={idleLocked} onClick={onEstudianteExtra}>
						<FontAwesomeIcon icon={solid("user-plus")} />
						Estudiante extra
					</button>
			}
			<button type="button" disabled={idleLocked} onClick={() => archivo.current?.click()}>
				<FontAwesomeIcon icon={solid("camera")} />
				{(uploadDocuments || []).length === 0 ? "Subir foto" : "Agregar otra foto"}
			</button>
			{
				(descargables || []).map((documento : any) =>
					<button key={"D_" + documento.id} type="button" disabled={idleLocked}
						onClick={() => onDescargar && onDescargar(documento)}
					>
						<FontAwesomeIcon icon={solid("circle-down")} />
						{String(documento.name || "").length <= 18 ? documento.name : "Foto de la clase"}
					</button>
				)
			}
		</div>

		<div className="waPaseGuardar">
			<span className="waPaseRecado">
				{recado}
			</span>
			<button type="button" disabled={!habilitado} onClick={onGuardar}>
				Guardar
			</button>
		</div>
	</div>
}

export default PaseDeLista
